import {
  BodyText,
  FlexColumn,
  FlexRow,
  SvgIcon,
  TableRow,
  TableRowType
} from '@make-software/cspr-design';
import BaseTableHeader from 'app/components/common/base-table/base-table-header';
import Table from 'app/components/common/table/table';
import React, { useEffect, useState } from 'react';
import { generatePath, NavLink, useNavigate } from 'react-router-dom';
import { ErrorResult, Pagination } from 'app/organizations-api/types';
import { PaginationResponseType } from 'app/console-api';
import {
  APPS_DEALS_PATH,
  APPS_DEALS_VIEW_PATH,
  APPS_OFFERS_PATH,
  APPS_TASKS_CREATE_PATH,
  APPS_TASKS_VIEW_PATH,
  HOME_PATH
} from 'app/route/paths';
import { NewTaskManager, Task, TaskManagerApp } from 'make-traffic-integration-core';
import { PreviewListItem } from 'app/components/management/apps/preview/preview-list-item';
import { useTabsCtx } from 'app/components/management/apps/tabs-page-layout';
import { PrioritiesRequest, updateTaskPriority } from 'app/console-api/tasks/priorities';
import { customErrorMessage, statusCode } from 'app/constants';
import { getRewardsTypes } from 'app/console-api/apps/rewards';
import { RewardsType } from 'app/console-api/apps/types';
import {
  StyledConfirmButton,
  StyledTableFiltersBlock,
  TablePageContainer
} from 'app/components/common/common-styles';
import { SkeletonLoader } from 'app/components/common/skeleton-loader/skeleton-loader';
import { PreloadTableWindow } from 'app/components/common/preload-table-window/preload-table-window';
import NoFound from 'assets/logos/no-found.svg';
import { StyledInput } from 'app/components/common/common-styles';
import SuccessEntityWindow from 'app/components/common/success-entity-modal/success-entity-modal';
import CreateSuccessIcon from 'assets/logos/success-entity.svg';
import ErrorEntityWindow from 'app/components/common/error-entity-modal/error-entity-modal';
import NotFoundIcon from 'assets/logos/no-found.svg';
import { useUrlFilterController } from 'app/components/filters/url-filters';
import { EntityTypeAdapter, EntityTypeFilter } from 'app/components/filters/entity-type-filter';
import { TaskTypeAdapter, TaskTypeFilter } from 'app/components/filters/task-type-filter';
import { AppCategoriesFilter, CategoriesAdapter } from 'app/components/filters/categories-filter';
import { AppEnvs } from 'config/env';
import { TaskClaimModalHost } from 'app/components/management/apps/preview/task-claim-modal';

export const AppPreview = () => {
  const filterController = useUrlFilterController();
  const { app } = useTabsCtx();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<Pagination>({ currentPage: 1, pageSize: 5 });
  const [unexpectedError, setUnexpectedError] = useState<ErrorResult | null>(null);
  const [tasksList, setTasksList] = useState<PaginationResponseType<Task> | null>(null);
  const [rewardsTypes, setRewardsTypes] = useState<RewardsType[]>([]);
  const lastFetchedFiltersKeyRef = React.useRef<string | null>(null);

  const [testUserIDInput, setTestUserIDInput] = useState<string>('');
  const [testUserID, setTestUserID] = useState<string>('');
  const [taskManager, setTaskManager] = useState<TaskManagerApp | null>(null);
  const [conflictError, setConflictError] = useState<string>('');
  const [openSuccessModal, setOpenSuccessModal] = useState<boolean>(false);
  const [openConflictErrorModal, setOpenConflictErrorModal] = useState<boolean>(false);

  async function refreshTaskListWith(manager: TaskManagerApp) {
    setLoading(true);
    try {
      const data = await manager.getTasks(testUserID, {
        entityType: filterController.get(EntityTypeAdapter) || 'all',
        pluginIds: filterController.get(TaskTypeAdapter) || undefined,
        categories: filterController.get(CategoriesAdapter) || undefined,
        page: pagination.currentPage,
        pageSize: pagination.pageSize
      });
      setTasksList(data);
    } catch (error: any) {
      setUnexpectedError({
        code: error?.code || 'Error',
        message: error?.message || 'Something went wrong'
      });
    } finally {
      setLoading(false);
    }
  }

  // Create the manager once per `app`
  useEffect(() => {
    if (!app) return;

    let cancelled = false;

    (async () => {
      try {
        if (cancelled) return;
        const manager = await NewTaskManager({
          apiUrl: process.env.REACT_APP_TRAFFIC_EXCHANGE_INTEGRATION_API_URL || '',
          appKey: app.appKey
        });
        setTaskManager(manager);
        await refreshTaskListWith(manager);
      } catch (e: any) {
        if (!cancelled) {
          setUnexpectedError({
            code: e?.code || 'InitError',
            message: e?.message || 'Failed to initialize'
          });
        }
      }
    })();

    return () => {
      cancelled = true;
      // Optional: manager.destroy?.();
    };
  }, [app]);

  // Refresh when inputs change and the manager is ready
  useEffect(() => {
    if (!taskManager) return;
    const filtersKey = filterController.buildCacheKey([
      EntityTypeAdapter,
      TaskTypeAdapter,
      CategoriesAdapter
    ]);
    if (lastFetchedFiltersKeyRef.current !== filtersKey) {
      setPagination((p) => (p.currentPage === 1 ? p : { ...p, currentPage: 1 }));
      pagination.currentPage = 1;
    }
    lastFetchedFiltersKeyRef.current = filtersKey;
    refreshTaskListWith(taskManager);
    getRewardsTypes(app!.id).then(
      (response) => setRewardsTypes(response.data || []),
      (error: any) => {
        console.error('Failed to fetch rewards types', error);
      }
    );
  }, [pagination, testUserID, taskManager, filterController]);

  const handlePageActions = (page: number, limit: number) => {
    if (pagination.pageSize != limit) {
      page = 1;
    }
    setPagination((prev) => ({
      ...prev,
      currentPage: page,
      pageSize: limit
    }));
  };

  const handleReorder = (newOrder: Task[]) => {
    const data = [] as PrioritiesRequest[];
    newOrder.map((task, idx) => {
      const { currentPage, pageSize } = pagination;
      const startIndex = (currentPage - 1) * pageSize; // zero-based offset
      const rowNum = startIndex + idx + 1;
      data.push({ EntityID: task.id, Value: rowNum });
    });
    updateTaskPriority(app!.id, data)
      .then(() => console.log('Task priority updated successfully'))
      .catch((error: any) => {
        console.error('Failed to update task priority:', error);
        setUnexpectedError({
          code: statusCode.unexpected_error.toString(),
          message: customErrorMessage.message
        });
      });
  };

  const viewTask = (data: Task) => {
    if (data.dealType == 'internal') {
      navigate(generatePath(APPS_TASKS_VIEW_PATH, { appID: app!.id, id: data.id }));
    } else {
      navigate(generatePath(APPS_DEALS_VIEW_PATH, { appID: app!.id, id: data.id }));
    }
  };

  const noTasks = !tasksList?.data?.length && !loading && !unexpectedError;

  const handleGoButton = (task: Task) => {
    taskManager
      ?.goProcess(testUserID, task)
      .then(() => setOpenSuccessModal(true))
      .catch((error: any) => {
        if (error.code === 409) {
          setConflictError('Cannot start the task');
          setOpenConflictErrorModal(true);
        } else {
          setUnexpectedError({
            code: error.code || 'Error',
            message: error.message || 'Something went wrong'
          });
        }
      });
  };

  const handleCloseSuccessModal = () => {
    setOpenSuccessModal(false);
    refreshTaskListWith(taskManager!);
  };

  const handleCloseConflictErrorModal = () => {
    setOpenConflictErrorModal(false);
    setConflictError('');
    refreshTaskListWith(taskManager!);
  };

  const handleClaimButton = (task: Task) => {
    taskManager
      ?.claimProcess(testUserID, task)
      .then(() => setOpenSuccessModal(true))
      .catch((error: any) => {
        console.log(error, error.message, error.code, error.info);
        if (error.code === 409) {
          setConflictError('Cannot claim the task, task is not completed or already claimed');
          setOpenConflictErrorModal(true);
        } else {
          setConflictError(error.message || 'Something went wrong');
          setOpenConflictErrorModal(true);
        }
      });
  };

  return (
    <TablePageContainer>
      <TaskClaimModalHost taskManager={taskManager} />
      <SuccessEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseSuccessModal}
        isOpen={openSuccessModal}
        icon={<SvgIcon src={CreateSuccessIcon} width={195} height={120} />}
        title="Task action successful"
        information="Task action successfully executed"
      />
      <ErrorEntityWindow
        dismissLabel={'Close'}
        onDismiss={handleCloseConflictErrorModal}
        isOpen={openConflictErrorModal}
        icon={<SvgIcon src={NotFoundIcon} width={195} height={120} />}
        title={conflictError}
        information={''}
      />
      <FlexColumn>
        <StyledTableFiltersBlock>
          {loading ? (
            <SkeletonLoader height={'35px'} />
          ) : (
            <>
              <EntityTypeFilter />
              <TaskTypeFilter />
              <AppCategoriesFilter appID={app!.id}></AppCategoriesFilter>
              {(AppEnvs.environment === 'dev' || AppEnvs.environment === 'local') && (
                <FlexRow gap={8} align="center">
                  <StyledInput
                    width="100"
                    placeholder="Test user ID"
                    value={testUserIDInput}
                    onChange={(e) => setTestUserIDInput(e.target.value)}
                  />
                  <StyledConfirmButton width={'100'} onClick={() => setTestUserID(testUserIDInput)}>
                    Test
                  </StyledConfirmButton>
                </FlexRow>
              )}
            </>
          )}
        </StyledTableFiltersBlock>
        {noTasks ? (
          <PreloadTableWindow
            icon={<SvgIcon src={NoFound} height={120} width={224} />}
            title={'No tasks yet'}
            windowTitle=""
            subtitle={
              <BodyText size={3}>
                Create a{' '}
                <NavLink to={generatePath(APPS_TASKS_CREATE_PATH, { appID: app!.id })}>
                  Task
                </NavLink>{' '}
                or import from{' '}
                <NavLink to={generatePath(HOME_PATH, { appID: app!.id })}>Marketplace</NavLink> and
                based on your{' '}
                <NavLink to={generatePath(APPS_OFFERS_PATH, { appID: app!.id })}>
                  Traffic offers
                </NavLink>{' '}
                and <NavLink to={generatePath(APPS_DEALS_PATH, { appID: app!.id })}>Deals</NavLink>.
              </BodyText>
            }
            onSubmitTitle="Create new"
          />
        ) : (
          <Table
            loading={loading}
            data={tasksList?.data || []}
            pageCount={tasksList?.page_count || 1}
            error={unexpectedError}
            pageSize={pagination.pageSize}
            currentPage={pagination.currentPage}
            handlePageActions={handlePageActions}
            tableRowType={TableRowType.TextSingleLine}
            renderDataHeader={() => (
              <TableRow>
                <BaseTableHeader fixedWidthRem={1}>#</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={25}>Target task</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={25}>Details</BaseTableHeader>
                <BaseTableHeader fixedWidthRem={25}>Rewards</BaseTableHeader>
                {testUserID !== '' && (
                  <BaseTableHeader fixedWidthRem={15}>User state & actions</BaseTableHeader>
                )}
                <BaseTableHeader align="right">Actions</BaseTableHeader>
              </TableRow>
            )}
            renderPaginatedData={(one) => {
              return one.map((task) => (
                <PreviewListItem
                  key={task.id}
                  data={task}
                  rewardsTypes={rewardsTypes}
                  testUserID={testUserID}
                  handleGoButton={handleGoButton}
                  handleClaimButton={handleClaimButton}
                  onView={viewTask}
                />
              ));
            }}
            dragNDropConfig={{
              getID: (task: Task) => task.id,
              onReorder: handleReorder
            }}
          />
        )}
      </FlexColumn>
    </TablePageContainer>
  );
};
